/**
 * Direct Verification Panel
 * Full verification UI with:
 * - Channel selector (CAMMIS Direct / Clearinghouse / Pre-Screen)
 * - 271 Response viewer with smart parser results
 * - Aid code intelligence display
 * - MCP routing information
 */

import React, { useState, useEffect, useCallback } from 'react';
import { eligibilityApi, EligibilityResult, CredentialStatus } from '@/services/api/eligibility';
import { apiClient } from '@/services/apiClient';
import EligibilityResultCard from '@/components/eligibility/EligibilityResultCard';
import { useTabbedChrome } from '@/components/weavein';

interface MemberSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  medicaid_id?: string;
  date_of_birth?: string;
  county?: string;
}

const DirectVerificationPanel: React.FC = () => {
  const tabbed = useTabbedChrome();
  // Member search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Channel & verification
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [credentialStatus, setCredentialStatus] = useState<CredentialStatus | null>(null);
  const [verificationResult, setVerificationResult] = useState<EligibilityResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  // History
  const [history, setHistory] = useState<any[]>([]);

  // MCP ECM info
  const [mcpInfo, setMcpInfo] = useState<any>(null);

  useEffect(() => {
    loadCredentialStatus();
  }, []);

  const loadCredentialStatus = async () => {
    try {
      const response = await eligibilityApi.getCredentialStatus();
      setCredentialStatus(response.data);
      // Auto-select best available channel
      const channels = response.data.channels_available || [];
      if (channels.includes('DIRECT_CAMMIS')) setSelectedChannel('DIRECT_CAMMIS');
      else if (channels.includes('STEDI')) setSelectedChannel('STEDI');
      else if (channels.includes('CLEARINGHOUSE')) setSelectedChannel('CLEARINGHOUSE');
      else setSelectedChannel('PRE_SCREEN');
    } catch {
      setSelectedChannel('PRE_SCREEN');
    }
  };

  // Debounced member search
  const searchMembers = useCallback(async (query: string) => {
    if (query.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    setSearching(true);
    try {
      const response = await apiClient.get('/api/members', { params: { search: query, limit: 10 } });
      const members = response.data?.members || response.data || [];
      setSearchResults(Array.isArray(members) ? members : []);
      setShowDropdown(true);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (searchQuery) searchMembers(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchMembers]);

  const handleSelectMember = (member: MemberSearchResult) => {
    setSelectedMember(member);
    setSearchQuery(`${member.first_name} ${member.last_name}`);
    setShowDropdown(false);
    setVerificationResult(null);
    setMcpInfo(null);
    setError('');
    // Load existing history
    loadHistory(member.id);
  };

  const loadHistory = async (memberId: number) => {
    try {
      const response = await eligibilityApi.getMemberHistory(memberId);
      setHistory(response.data?.verifications || []);
    } catch { setHistory([]); }
  };

  const handleVerify = async () => {
    if (!selectedMember) { setError('Select a member first'); return; }
    setVerifying(true);
    setError('');
    setMcpInfo(null);

    try {
      const response = await eligibilityApi.verify({
        member_id: selectedMember.id,
        reason: 'MANUAL',
        force_channel: selectedChannel || undefined,
      });
      setVerificationResult(response.data);
      loadHistory(selectedMember.id);

      // If MCP identified, fetch ECM info
      if (response.data.mcp_plan_name) {
        try {
          const mcpResp = await eligibilityApi.getMCPECMInfo(response.data.mcp_plan_name);
          setMcpInfo(mcpResp.data);
        } catch { /* MCP info not available */ }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleECMVerifyAndRoute = async () => {
    if (!selectedMember) return;
    setVerifying(true);
    setError('');
    try {
      const response = await eligibilityApi.ecmVerifyAndRoute(selectedMember.id);
      setVerificationResult(response.data.eligibility);
      setMcpInfo(response.data.mcp_ecm_info);
      loadHistory(selectedMember.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'ECM verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const channelConfig: Record<string, { label: string; description: string; cost: string; color: string }> = {
    DIRECT_CAMMIS: { label: 'CAMMIS Direct', description: 'Direct to DHCS — authoritative, requires Form 6153', cost: '$0/query', color: 'border-green-300 bg-green-50' },
    STEDI: { label: 'Stedi', description: 'JSON API — no enrollment for CA Medi-Cal 270', cost: '$0.15/query', color: 'border-indigo-300 bg-indigo-50' },
    CLEARINGHOUSE: { label: 'Availity', description: 'OAuth2 REST API — requires payer enrollment', cost: '~$0.15/query', color: 'border-blue-300 bg-blue-50' },
    PRE_SCREEN: { label: 'Pre-Screen Only', description: 'FPL estimation (no external call)', cost: '$0', color: 'border-gray-300 bg-gray-50' },
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div>
        {!tabbed && (
        <h2 className="text-lg font-semibold">Direct Eligibility Verification</h2>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Verify Medi-Cal eligibility with channel selection, 271 response parsing, and MCP identification.
        </p>
      </div>

      {/* Member Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Search Member</label>
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, Medicaid ID, or phone..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); if (!e.target.value) { setSelectedMember(null); setVerificationResult(null); setHistory([]); } }}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            className="w-full border rounded-md px-3 py-2 text-sm"
          />
          {searching && <span className="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</span>}
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map(member => (
                <button key={member.id} type="button" onClick={() => handleSelectMember(member)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-sm">
                  <div className="font-medium">{member.first_name} {member.last_name}</div>
                  <div className="text-xs text-gray-500">
                    {member.medicaid_id && <span>CIN: {member.medicaid_id} | </span>}
                    {member.date_of_birth && <span>DOB: {member.date_of_birth} | </span>}
                    {member.county && <span>County: {member.county}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedMember && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between text-sm">
            <div>
              <span className="font-medium">{selectedMember.first_name} {selectedMember.last_name}</span>
              {selectedMember.medicaid_id && <span className="ml-2 text-xs text-gray-600">CIN: {selectedMember.medicaid_id}</span>}
              {selectedMember.county && <span className="ml-2 text-xs text-gray-600">County: {selectedMember.county}</span>}
            </div>
            <button onClick={() => { setSelectedMember(null); setSearchQuery(''); setVerificationResult(null); setHistory([]); }}
              className="text-xs text-gray-500 hover:text-red-600">Clear</button>
          </div>
        )}
      </div>

      {/* Channel Selector + Verify */}
      {selectedMember && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Verification Channel</h3>
            <div className="flex gap-2">
              <button onClick={handleVerify} disabled={verifying}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 font-medium">
                {verifying ? 'Verifying...' : 'Verify Eligibility'}
              </button>
              <button onClick={handleECMVerifyAndRoute} disabled={verifying}
                className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm hover:bg-purple-700 disabled:opacity-50">
                ECM Verify & Route
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {Object.entries(channelConfig).map(([key, config]) => {
              const available = credentialStatus?.channels_available?.includes(key) || key === 'PRE_SCREEN';
              return (
                <button
                  key={key}
                  onClick={() => available && setSelectedChannel(key)}
                  disabled={!available}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedChannel === key ? `${config.color} border-opacity-100 ring-2 ring-blue-400` :
                    available ? 'border-gray-200 hover:border-gray-300' : 'border-gray-100 opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div className="text-sm font-medium">{config.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{config.description}</div>
                  <div className="text-xs font-medium mt-1">{config.cost}</div>
                  {!available && <div className="text-xs text-red-400 mt-1">Not configured</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{error}</div>}

      {/* Verification Result */}
      {verificationResult && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold mb-3">Verification Result</h3>
            <EligibilityResultCard result={verificationResult} />
          </div>

          {/* Smart Parser: Aid Code Intelligence */}
          {verificationResult.aid_code && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold mb-3">Aid Code Intelligence</h3>
              <AidCodeDetail aidCode={verificationResult.aid_code} scope={verificationResult.benefits_scope} />
            </div>
          )}

          {/* MCP Routing */}
          {mcpInfo && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold mb-3">MCP Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Plan:</span> <span className="font-medium">{mcpInfo.plan_name}</span></div>
                {mcpInfo.phone_number && <div><span className="text-gray-500">Phone:</span> {mcpInfo.phone_number}</div>}
                {mcpInfo.website && (
                  <div><span className="text-gray-500">Website:</span>{' '}
                    <a href={mcpInfo.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{mcpInfo.website}</a>
                  </div>
                )}
                {mcpInfo.counties && (
                  <div><span className="text-gray-500">Counties:</span> {mcpInfo.counties.map((c: any) => c.county_name).join(', ')}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold mb-3">Verification History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Aid Code</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Scope</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">MCP</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Channel</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h: any) => (
                  <tr key={h.verification_id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs">{h.verified_at?.split('T')[0]}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        h.eligibility_status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                        h.eligibility_status === 'ESTIMATED' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>{h.eligibility_status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{h.aid_code || '—'}</td>
                    <td className="px-3 py-2 text-xs">{h.benefits_scope?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[120px]">{h.mcp_plan_name || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{h.query_channel?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs text-gray-400">{h.response_time_ms ? `${h.response_time_ms}ms` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/** Aid Code Intelligence Sub-Component */
const AidCodeDetail: React.FC<{ aidCode: string; scope: string }> = ({ aidCode, scope }) => {
  const [detail, setDetail] = useState<any>(null);

  useEffect(() => {
    loadAidCode();
  }, [aidCode]);

  const loadAidCode = async () => {
    try {
      const response = await eligibilityApi.getAidCodes();
      const codes = response.data?.aid_codes || [];
      const match = codes.find((c: any) => c.aid_code === aidCode);
      if (match) setDetail(match);
    } catch { /* fallback below */ }
  };

  const scopeColor: Record<string, string> = {
    FULL_SCOPE: 'bg-green-100 text-green-800',
    RESTRICTED: 'bg-yellow-100 text-yellow-800',
    EMERGENCY_ONLY: 'bg-red-100 text-red-800',
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="p-3 bg-gray-50 rounded">
        <div className="text-xs text-gray-500">Aid Code</div>
        <div className="text-lg font-bold font-mono">{aidCode}</div>
      </div>
      <div className="p-3 bg-gray-50 rounded">
        <div className="text-xs text-gray-500">Benefits Scope</div>
        <span className={`text-sm font-medium px-2 py-0.5 rounded ${scopeColor[scope] || 'bg-gray-100'}`}>
          {scope?.replace(/_/g, ' ')}
        </span>
      </div>
      {detail && (
        <>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500">Program</div>
            <div className="text-sm font-medium">{detail.program_name}</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500">Category</div>
            <div className="text-sm">{detail.program_category} {detail.magi_based ? '(MAGI)' : '(Non-MAGI)'}</div>
          </div>
        </>
      )}
      {detail?.description && (
        <div className="col-span-full p-3 bg-gray-50 rounded">
          <div className="text-xs text-gray-500">Description</div>
          <div className="text-sm">{detail.description}</div>
          {detail.has_share_of_cost && (
            <div className="text-xs text-amber-600 mt-1">This program requires a monthly Share of Cost payment.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DirectVerificationPanel;
